// ─── Color Utilities ────────────────────────────────────────────────────────

export function hexesAreEqual(hex1?: string, hex2?: string): boolean {
  if (!hex1 || !hex2) return false
  return hex1.toLowerCase() === hex2.toLowerCase()
}

function hexToHsl(hex: string): [number, number, number] {
  let r = parseInt(hex.slice(1, 3), 16) / 255
  let g = parseInt(hex.slice(3, 5), 16) / 255
  let b = parseInt(hex.slice(5, 7), 16) / 255
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b)
  let h = 0,
    s = 0,
    l = (max + min) / 2
  if (max !== min) {
    const d = max - min
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
    switch (max) {
      case r:
        h = ((g - b) / d + (g < b ? 6 : 0)) / 6
        break
      case g:
        h = ((b - r) / d + 2) / 6
        break
      case b:
        h = ((r - g) / d + 4) / 6
        break
    }
  }
  return [h * 360, s * 100, l * 100]
}

function hslToHex(h: number, s: number, l: number): string {
  h /= 360
  s /= 100
  l /= 100
  let r = 0,
    g = 0,
    b = 0
  if (s === 0) {
    r = g = b = l
  } else {
    const hue2rgb = (p: number, q: number, t: number): number => {
      if (t < 0) t += 1
      if (t > 1) t -= 1
      if (t < 1 / 6) return p + (q - p) * 6 * t
      if (t < 1 / 2) return q
      if (t < 2 / 3) return p + (q - p) * (2 / 3 - t) * 6
      return p
    }
    const q = l < 0.5 ? l * (1 + s) : l + s - l * s
    const p = 2 * l - q
    r = hue2rgb(p, q, h + 1 / 3)
    g = hue2rgb(p, q, h)
    b = hue2rgb(p, q, h - 1 / 3)
  }
  return (
    '#' +
    [r, g, b]
      .map(x =>
        Math.round(x * 255)
          .toString(16)
          .padStart(2, '0')
      )
      .join('')
  )
}

/**
 * Adjust the lightness of a hex color.
 * Positive pct lightens (moves toward white), negative pct darkens (moves toward black).
 * Both are proportional — e.g. +30% moves 30% of the remaining distance to white.
 *
 * @param hex - A 6-digit hex color string, e.g. "#3B6D11"
 * @param pct - Percentage to adjust, -100 to 100
 */
export function adjustLightness(hex: string, pct: number): string {
  const [h, s, l] = hexToHsl(hex)
  const newL =
    pct >= 0 ? Math.min(100, l + (100 - l) * (pct / 100)) : Math.max(0, l + l * (pct / 100))
  return hslToHex(h, s, newL)
}

// Nudges the preview color when thread matches background so it remains visible.
// Does not affect the actual thread color sent to OMS.
export function getEmbroideryNudgedColor(
  backgroundColor: string | undefined,
  textColorCss: string | undefined
): string | undefined {
  // Both background and thread are white — darken thread for visibility; otherwise lighten it
  const adjustmentPct =
    backgroundColor?.toLowerCase() === '#ffffff' && textColorCss?.toLowerCase() === '#ffffff'
      ? -15
      : 15

  return hexesAreEqual(backgroundColor, textColorCss) && textColorCss
    ? adjustLightness(textColorCss, adjustmentPct)
    : textColorCss
}
